import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Bell, Timer } from "lucide-react";
import { useGtt } from "../lib/gtt";
import { useMarket } from "../lib/store";
import { useSubscribe, useTick, useMeta } from "../lib/socket";
import { useWatchlists } from "../lib/watchlists";
import { signed, pct, tone, fmt } from "../lib/format";
import { Price, ExTag } from "../components/Price";
import { PriceChart } from "../components/PriceChart";
import { MarketDepth } from "../components/MarketDepth";
import { DiwaliStrip } from "../components/diwali/DiwaliOffer";
import { QuoteDecode } from "../components/motion";

function DecodedPrice({ k, t }) {
  const [done, setDone] = useState(null);
  const has = t?.ltp != null;
  useEffect(() => { if (!has || done === k) return undefined; const id = setTimeout(() => setDone(k), 900); return () => clearTimeout(id); }, [has, k, done]);
  if (done === k) return <Price value={t?.ltp} prev={t?.prevLtp} testid="instrument-ltp" />;
  return <QuoteDecode text={fmt(t?.ltp)} runKey={has ? k : null} testid="instrument-ltp" />;
}

export default function InstrumentPage() {
  const { key: raw } = useParams();
  const key = decodeURIComponent(raw);
  useSubscribe([key]);
  const t = useTick(key);
  const inst = useMeta(key);
  const { openOrder, setFocus } = useMarket();
  const add = useWatchlists((s) => s.add);
  const { openGtt, openAlert } = useGtt();
  useEffect(() => { setFocus(key); }, [key, setFocus]);
  const tradable = inst && inst.instrument_type !== "INDEX";
  return (
    <div className="flex flex-col lg:flex-row h-full min-h-[600px]" data-testid="instrument-page">
      <div className="flex-1 flex flex-col min-w-0 border-r border-border">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-xl" data-testid="instrument-symbol"><QuoteDecode text={inst?.trading_symbol || key} runKey={inst ? key : null} className="!font-display" /></h1>
              <ExTag ex={inst?.exchange} />
            </div>
            <div className="text-[11px] text-muted-foreground">{inst?.name}{inst?.lot_size > 1 ? ` · Lot ${inst.lot_size}` : ""}{inst?.expiry ? ` · Exp ${inst.expiry}` : ""}</div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`text-[22px] font-semibold ${tone(t?.ch)}`}><DecodedPrice k={key} t={t} /></span>
            <span className={`num text-[12px] ${tone(t?.ch)}`}>{signed(t?.ch)} ({pct(t?.chp)})</span>
          </div>
          <div className="flex-1" />
          <div className="flex gap-2">
            <button data-testid="instrument-add-watchlist" onClick={() => add(key)} className="h-8 px-3 rounded-md border border-border text-[12px] flex items-center gap-1 hover:bg-accent"><Plus className="h-3.5 w-3.5" />Watchlist</button>
            {inst && <button data-testid="instrument-alert-button" onClick={() => openAlert({ key })} className="h-8 px-3 rounded-md border border-border text-[12px] flex items-center gap-1 hover:bg-accent"><Bell className="h-3.5 w-3.5" />Alert</button>}
            {tradable && <button data-testid="instrument-gtt-button" onClick={() => openGtt({ key })} className="h-8 px-3 rounded-md border border-border text-[12px] flex items-center gap-1 hover:bg-accent"><Timer className="h-3.5 w-3.5" />GTT</button>}
            {tradable && (<>
              <button data-testid="instrument-buy-button" onClick={() => openOrder({ key, side: "BUY" })} className="h-8 px-5 rounded-md bg-up text-white font-semibold text-[12px] hover:brightness-110">Buy</button>
              <button data-testid="instrument-sell-button" onClick={() => openOrder({ key, side: "SELL" })} className="h-8 px-5 rounded-md bg-down text-white font-semibold text-[12px] hover:brightness-110">Sell</button>
            </>)}
          </div>
        </div>
        <div className="flex-1 min-h-[420px]"><PriceChart instrumentKey={key} /></div>
      </div>
      <aside className="lg:w-[320px] shrink-0 p-4 bg-card/40">
        <div className="mb-4 empty:hidden"><DiwaliStrip /></div>
        <div className="eyebrow mb-3">Market depth</div>
        <MarketDepth instrumentKey={key} />
      </aside>
    </div>
  );
}
